/**
 * @file PublishSiteDialog.tsx
 * @description Окно «Опубликовать» (phase19.16 Р1, Р1-6а; uiux-design-r1
 * §6, uiux-builder-shell.md §3.1; SB50, SB51, SB59, SB60, SB74).
 *
 * Что изменится в виде сайта (с авторами), проверки (реквизиты, оферта —
 * блокируют; контраст, скрытые пункты — предупреждают), затем страницы и
 * места с правками (Р3а, uiux-r3 §3.2, `PublishPagesSection`). Если вид
 * сайта есть вместе со страницами — у каждого флажок, публикуется
 * отмеченное: сначала вид, затем страницы одной пачкой. До sm — на весь
 * экран, кнопки внизу. Без права на чужие правки —
 * текст, чьи правки ждут и у кого есть право. Состояние — `usePublishFlow`.
 * SB125: при ошибках — галочка «публикую с незакрытыми проверками»; отказ
 * сервера по праву — текстом под кнопкой «Опубликовать». SB138: при закрытом
 * сайте — строка «Сайт сейчас закрыт — изменения увидят покупатели, когда вы
 * его откроете» (`SiteClosedPublishNote`).
 */

import { ExternalLink, X } from "lucide-react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SiteClosedPublishNote } from "../../site-access/SiteClosedPublishNote";
import { useSiteView } from "../site-view-context";
import { PublishPagesSection } from "./PublishPagesSection";
import { PublishViewSection } from "./PublishViewSection";
import { usePublishFlow } from "./usePublishFlow";

export function PublishSiteDialog() {
  const view = useSiteView();
  const flow = usePublishFlow();
  const { snapshot, withPages } = flow;
  const description = withPages
    ? "Отмеченное сразу попадёт на сайт, остальное останется в черновике. Покупатели увидят изменения в течение минуты."
    : "Покупатели увидят изменения в течение минуты.";
  const acknowledge = { checked: flow.acknowledged, onChange: flow.setAcknowledged, allowed: flow.canAcknowledge };

  return (
    <Dialog open={flow.open} onOpenChange={(next) => view.openDialog(next ? "publish" : null)}>
      {/* До sm поля окна 16: закреплённая шапка — только заголовок и «Закрыть» (≤ 120 px). */}
      <DialogContent fullScreen showClose={false} className="max-sm:gap-3 max-sm:p-4 sm:max-w-2xl">
        <DialogHeader className="flex-row items-center gap-2 sm:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <DialogTitle>Опубликовать</DialogTitle>
            {/* До sm описание прокручивается вместе со списком (копия ниже), здесь — для скринридера. */}
            <DialogDescription className="max-sm:sr-only">{description}</DialogDescription>
          </div>
          {/* Закрыть — область касания 48 (у общего крестика окна 24). */}
          <Button variant="ghost" size="icon" className="-mr-2 -mt-2 size-12 shrink-0" onClick={() => view.openDialog(null)} aria-label="Закрыть">
            <X aria-hidden />
          </Button>
        </DialogHeader>
        {flow.problem ? <NoticeStrip tone="warning">{flow.problem}</NoticeStrip> : null}
        {/* SB138: публиковать при закрытом сайте можно — покупатели увидят после открытия. */}
        <SiteClosedPublishNote />
        {!snapshot ? (
          <p className="type-body text-on-surface-variant" aria-busy="true">
            Проверяем черновик…
          </p>
        ) : (
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto max-sm:max-h-none">
            <p aria-hidden className="type-body text-on-surface-variant sm:hidden">
              {description}
            </p>
            {flow.viewChanged || !withPages ? (
              <PublishViewSection
                snapshot={snapshot}
                selectable={withPages}
                checked={flow.viewChecked && flow.viewPublishable}
                disabled={!flow.viewPublishable}
                onChecked={flow.setViewChecked}
                errors={flow.viewErrors.length}
                onRestore={flow.restore}
                acknowledge={acknowledge}
              />
            ) : null}
            <PublishPagesSection pages={flow.pages} acknowledge={acknowledge} />
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => view.openDialog("preview")}>
            <ExternalLink aria-hidden />
            Посмотреть в предпросмотре
          </Button>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="ghost" onClick={() => view.openDialog(null)}>
                Отмена
              </Button>
              <Button variant="brand" disabled={!flow.canSubmit} loading={flow.busy} onClick={() => void flow.onPublish()}>
                {flow.submitLabel}
              </Button>
            </div>
            {/* SB125: сервер не принял галочку (нет права) — его текст под кнопкой. */}
            {flow.acknowledgeError ? (
              <p role="alert" className="type-caption text-destructive sm:max-w-sm sm:self-end sm:text-right">
                {flow.acknowledgeError}
              </p>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
